import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';

import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { FormatDatasetAdapter } from 'app/model/adapters/datasets/format-dataset.adapter';
import { JsonDataset } from 'shell-models-lib';
import { HierarchicalNode } from 'app/model/dataset/hierarchicalnode';
import { compressionAdapts } from './file-type.dataset.adapter';
import { GlobalVariables } from 'app/services/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(JsonDataset),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            folder: '*',
            annotations: '*',
            schema: '*',
            parameters: '*',
            type: '*',
            linkedServiceName: '*',
            linkedService: '*',
            connectionSettings: __IS_FABRIC_DPE__ ? '*' : undefined,
            connectionProperties: GlobalVariables.disableServerCertificate ? '*' : undefined,
            typeProperties: {
                location: '*',
                compression: compressionAdapts,
                encodingName: '*'
            }
        }
    }
})
export class JsonDatasetAdapter extends FormatDatasetAdapter<JsonDataset, Model.DatasetV2Resource> {
    public parseHierarchicalSchema(obj: unknown): HierarchicalNode {
        try {
            return parseJsonSchema(obj, 'schema');
        } catch {
            return undefined;
        }
    }

    protected async _toResource(dataset: JsonDataset): Promise<Model.DatasetV2Resource> {
        const modelDatasetResource = await super._toResource(dataset) as Model.DatasetV2Resource;
        const modelDataset = this.properties(modelDatasetResource) as Model.JsonDataset;
        modelDataset.type = Metamodel.modelFor(JsonDataset).name;
        modelDataset.typeProperties = modelDataset.typeProperties || {};
        if (dataset.location) {
            modelDataset.typeProperties.location = this.locationToResource(dataset);
        }

        const compression = ValueAdapter.toResource(dataset, JsonDataset.COMPRESSION_CODEC);
        if (compression != null) {
            modelDataset.typeProperties.compression = {
                type: compression,
                level: ValueAdapter.toResource(dataset, JsonDataset.COMPRESSION_LEVEL)
            };
        }
        modelDataset.typeProperties.encodingName = ValueAdapter.toResource(dataset, JsonDataset.ENCODING_NAME);

        modelDataset.schema = dataset.rawHierarchicalSchema ? dataset.rawHierarchicalSchema : {};

        if (dataset.raw) {
            const parsed = JSON.parse(dataset.raw);
            _.merge(modelDatasetResource, parsed);
        }
        return modelDatasetResource;
    }

    protected async _toModel(modelDatasetResource: Model.DatasetV2Resource, dataset?: JsonDataset): Promise<JsonDataset> {
        if (!dataset) {
            dataset = JsonDataset.MODEL.create<JsonDataset>(modelDatasetResource.name);
        }
        await super._toModel(modelDatasetResource, dataset);
        if (modelDatasetResource
            && modelDatasetResource.properties
            && modelDatasetResource.properties.typeProperties
            && modelDatasetResource.properties.typeProperties.location) {
            await this.locationToModel(modelDatasetResource, dataset);
        }

        if (modelDatasetResource.properties.typeProperties != null) {
            if (modelDatasetResource.properties.typeProperties.compression != null) {
                ValueAdapter.toModel(dataset, JsonDataset.COMPRESSION_CODEC, modelDatasetResource.properties.typeProperties.compression.type);
                ValueAdapter.toModel(dataset, JsonDataset.COMPRESSION_LEVEL, modelDatasetResource.properties.typeProperties.compression.level);
            }
            ValueAdapter.toModel(dataset, JsonDataset.ENCODING_NAME, modelDatasetResource.properties.typeProperties.encodingName);
        }

        dataset.rawHierarchicalSchema = modelDatasetResource.properties.schema;
        if (dataset.rawHierarchicalSchema != null) {
            dataset.schemas = this.parseHierarchicalSchema(dataset.rawHierarchicalSchema);
        }
        dataset.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelDatasetResource, JsonDataset.MODEL));
        return dataset;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseJsonSchema(obj: any, name?: string): HierarchicalNode {
    const node = new HierarchicalNode(name);
    node.columnType = obj != null ? obj.type : 'any';
    if (node.isObject) {
        node.properties = {};
        Object.keys(obj.properties || {}).forEach((k) => {
            node.properties[k] = parseJsonSchema(obj.properties[k], k);
        });
    } else if (node.isArray) {
        node.items = parseJsonSchema(obj.items, 'items');
    }
    return node;
}
